import React, { useEffect, useRef } from 'react';

const CHAPTER_ATTRIBUTE = 'data-chapter-index';
const OBSERVER_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];
const OBSERVER_ROOT_MARGIN = '0px';
const CHAPTER_INDEX_PATTERN = /^\d+$/;
const PASSIVE_LISTENER_OPTIONS = { passive: true };

/**
 * Collects direct child chapters with unique, strict non-negative indices.
 *
 * @param {Element} container - Guided scroll container whose children are inspected.
 * @returns {Array<{element: Element, index: number}>} Valid chapter/index pairs
 *   in document order, excluding malformed and repeated indices.
 * @sideEffects Has no side effects; it only reads the supplied DOM element.
 */
export const getValidChapterEntries = (container) => {
  const seenIndices = new Set();

  return Array.from(container.children).reduce((entries, element) => {
    const value = element.getAttribute(CHAPTER_ATTRIBUTE);

    if (value === null || !CHAPTER_INDEX_PATTERN.test(value)) {
      return entries;
    }

    const index = Number(value);

    if (!Number.isSafeInteger(index) || seenIndices.has(index)) {
      return entries;
    }

    seenIndices.add(index);
    entries.push({ element, index });
    return entries;
  }, []);
};

/**
 * Renders the normal-document-flow landmark that coordinates guided chapters.
 *
 * @param {object} props - Guided scroll configuration.
 * @param {React.ReactNode} props.children - Direct chapter elements carrying a
 *   numeric `data-chapter-index` attribute.
 * @param {(index: number) => void} [props.onIndexChange] - Receives the active
 *   chapter index as the reader moves forward or backward.
 * @returns {React.ReactElement} A single main landmark containing the chapters.
 * @sideEffects Observes native document scrolling to report the dominant
 *   chapter and cleans up observers or passive fallback listeners on unmount.
 */
export default function GuidedScroll({ children, onIndexChange }) {
  const containerRef = useRef(null);
  const onIndexChangeRef = useRef(onIndexChange);
  const lastReportedIndexRef = useRef(null);

  onIndexChangeRef.current = onIndexChange;

  useEffect(() => {
    const container = containerRef.current;

    if (!container || typeof window === 'undefined') {
      return undefined;
    }

    const chapterEntries = getValidChapterEntries(container);
    const reportIndex = (index) => {
      let targetIndex = index;
      if (window.scrollY > 5 && targetIndex === 0) {
        targetIndex = 1;
      }

      if (lastReportedIndexRef.current === targetIndex) {
        return;
      }

      lastReportedIndexRef.current = targetIndex;
      onIndexChangeRef.current?.(targetIndex);
    };

    if (typeof window.IntersectionObserver === 'function') {
      const indexByElement = new Map(chapterEntries.map(({ element, index }) => (
        [element, index]
      )));
      const visibilityByElement = new Map(chapterEntries.map(({ element }) => (
        [element, 0]
      )));
      const observer = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          visibilityByElement.set(
            entry.target,
            entry.isIntersecting ? entry.intersectionRatio : 0,
          );
        });

        const dominantChapter = chapterEntries.reduce((dominant, entry) => {
          const visibility = visibilityByElement.get(entry.element) || 0;

          return !dominant || visibility > dominant.visibility
            ? { element: entry.element, visibility }
            : dominant;
        }, null);

        if (dominantChapter?.visibility > 0) {
          reportIndex(indexByElement.get(dominantChapter.element));
        }
      }, {
        root: null,
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: OBSERVER_THRESHOLDS,
      });

      chapterEntries.forEach(({ element }) => observer.observe(element));

      const handleScroll = () => {
        const scrollY = window.scrollY;
        if (scrollY <= 5) {
          reportIndex(0);
        } else if (lastReportedIndexRef.current === 0 || lastReportedIndexRef.current === null) {
          reportIndex(1);
        }
      };

      window.addEventListener('scroll', handleScroll, PASSIVE_LISTENER_OPTIONS);

      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', handleScroll);
      };
    }

    const reportClosestChapter = () => {
      const viewportCenter = window.innerHeight / 2;
      const closestChapter = chapterEntries.reduce((closest, entry) => {
        const bounds = entry.element.getBoundingClientRect();
        const distance = Math.abs(((bounds.top + bounds.bottom) / 2) - viewportCenter);

        return !closest || distance < closest.distance
          ? { distance, index: entry.index }
          : closest;
      }, null);

      if (closestChapter) {
        reportIndex(closestChapter.index);
      }
    };

    window.addEventListener('scroll', reportClosestChapter, PASSIVE_LISTENER_OPTIONS);
    window.addEventListener('resize', reportClosestChapter, PASSIVE_LISTENER_OPTIONS);
    reportClosestChapter();

    return () => {
      window.removeEventListener('scroll', reportClosestChapter);
      window.removeEventListener('resize', reportClosestChapter);
    };
  }, []);

  return (
    <main ref={containerRef} className="guided-scroll">
      {children}
    </main>
  );
}
